import { useQuery } from '@tanstack/react-query';
import { Wallet, Brain } from 'lucide-react';
import { dashboardApi } from '@/api/dashboard';
import type { AgentCostBreakdown, DailyCostSummary } from '@/api/types';
import { cn } from '@/lib/cn';
import { ago } from '@/lib/format';

const usd = (n: number | null | undefined, decimals = 2): string => {
  if (n == null || Number.isNaN(n)) return '—';
  return `$${n.toFixed(decimals)}`;
};

const fmtTokens = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
};

export function Cost() {
  const { data, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ['costDaily'],
    queryFn: () => dashboardApi.costDaily(),
    refetchInterval: 60_000,
  });

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-[10px] bg-surface-2 grid place-items-center text-text-dim">
          <Wallet size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold">Cost &amp; Tokens</h1>
          <div className="text-xs text-text-faint">
            {isLoading ? 'loading…'
              : isError ? 'unable to reach /api/cost/daily'
              : data
                ? `${data.from} → ${data.to} · ${data.daysWithData} days · refreshed ${ago(new Date(dataUpdatedAt).toISOString())}`
                : ''}
          </div>
        </div>
      </div>

      {data && (
        <div className="space-y-6 max-w-5xl">
          {/* Headline aggregates */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Total spend" value={usd(data.totalCostUsd, 2)} />
            <Tile label="Avg / day" value={usd(data.avgDailyCostUsd, 2)} />
            <Tile label="Projected / mo" value={usd(data.projectedMonthlyCostUsd, 2)} subtle="× 21 trading days" />
            <Tile
              label="Thinking share"
              value={data.totalInputTokens + data.totalOutputTokens + data.totalThinkingTokens > 0
                ? `${((data.totalThinkingTokens / (data.totalInputTokens + data.totalOutputTokens + data.totalThinkingTokens)) * 100).toFixed(1)}%`
                : '—'}
              subtle={`${fmtTokens(data.totalThinkingTokens)} of ${fmtTokens(data.totalInputTokens + data.totalOutputTokens + data.totalThinkingTokens)}`}
            />
          </div>

          {/* Daily series */}
          <section>
            <h2 className="text-sm font-semibold mb-2 text-text-dim">Daily Spend</h2>
            {data.summaries.length === 0
              ? <div className="card card-tight text-xs text-text-faint italic">No cost snapshots yet — DailyCostScheduler writes one per trading day at 17:00 KST.</div>
              : <DailySpendChart summaries={data.summaries} />}
          </section>

          {/* Latest day per-agent breakdown */}
          {data.summaries[0] && (
            <section>
              <h2 className="text-sm font-semibold mb-2 text-text-dim">
                Per-Agent · {data.summaries[0].date}
              </h2>
              <PerAgentTable summary={data.summaries[0]} />
            </section>
          )}

          {/* Pulse + Tier2 efficiency */}
          {data.summaries[0] && <EfficiencySection summary={data.summaries[0]} />}
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, subtle }: { label: string; value: string; subtle?: string }) {
  return (
    <div className="card card-tight">
      <div className="text-xs text-text-faint uppercase tracking-wide mb-1">{label}</div>
      <div className="text-base font-semibold font-mono tabular-nums">{value}</div>
      {subtle && <div className="text-[10px] text-text-faint mt-0.5">{subtle}</div>}
    </div>
  );
}

function DailySpendChart({ summaries }: { summaries: DailyCostSummary[] }) {
  // summaries come desc; flip so the chart reads left-to-right oldest→newest.
  const rows = [...summaries].reverse();
  const max = Math.max(...rows.map((r) => r.totalCost), 0.0001);

  return (
    <div className="card card-tight">
      <div className="flex items-end gap-1 h-32 mb-2">
        {rows.map((r) => {
          const heightPct = (r.totalCost / max) * 100;
          const thinkShare = r.totalInputTokens + r.totalOutputTokens + r.totalThinkingTokens > 0
            ? r.totalThinkingTokens / (r.totalInputTokens + r.totalOutputTokens + r.totalThinkingTokens)
            : 0;
          return (
            <div
              key={r.date}
              className="flex-1 flex flex-col-reverse min-w-[6px] group relative cursor-default"
              title={`${r.date}\n$${r.totalCost.toFixed(4)}\nthinking ${(thinkShare * 100).toFixed(1)}%\n${r.tradesSubmitted} trades`}
            >
              <div className="w-full bg-kam-violet/60 rounded-sm" style={{ height: `${heightPct}%` }} />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-text-faint font-mono">
        <span>{rows[0]?.date}</span>
        <span>{rows[rows.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function PerAgentTable({ summary }: { summary: DailyCostSummary }) {
  const rows: Array<[string, AgentCostBreakdown]> = Object.entries(summary.perAgent ?? {})
    .sort((a, b) => b[1].cost - a[1].cost);

  if (rows.length === 0) {
    return <div className="card card-tight text-xs text-text-faint italic">No per-agent breakdown.</div>;
  }

  return (
    <div className="card card-tight overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-text-faint text-xs uppercase tracking-wide">
          <tr>
            <th className="text-left font-medium py-1.5 px-2">Agent</th>
            <th className="text-right font-medium py-1.5 px-2">Calls</th>
            <th className="text-right font-medium py-1.5 px-2">In</th>
            <th className="text-right font-medium py-1.5 px-2">Out</th>
            <th className="text-right font-medium py-1.5 px-2">
              <Brain size={11} className="inline mr-0.5" />Think
            </th>
            <th className="text-right font-medium py-1.5 px-2">Cost</th>
            <th className="text-right font-medium py-1.5 px-2">Median ms</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([agent, b]) => (
            <tr key={agent} className="border-t border-[color:var(--border)]">
              <td className="py-1.5 px-2 font-mono text-xs">{agent}</td>
              <td className="py-1.5 px-2 text-right tabular-nums">{b.calls}</td>
              <td className="py-1.5 px-2 text-right tabular-nums">{fmtTokens(b.inputTokens)}</td>
              <td className="py-1.5 px-2 text-right tabular-nums">{fmtTokens(b.outputTokens)}</td>
              <td className={cn(
                'py-1.5 px-2 text-right tabular-nums',
                b.thinkingTokens > 0 ? 'text-kam-violet' : 'text-text-faint',
              )}>
                {fmtTokens(b.thinkingTokens)}
                <span className="text-[10px] text-text-faint ml-1">
                  ({(b.adaptiveThinkingActivationRate * 100).toFixed(0)}%)
                </span>
              </td>
              <td className="py-1.5 px-2 text-right tabular-nums font-semibold">{usd(b.cost, 3)}</td>
              <td className="py-1.5 px-2 text-right tabular-nums text-text-dim">{b.medianLatencyMs}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EfficiencySection({ summary }: { summary: DailyCostSummary }) {
  const p = summary.pulse;
  const t = summary.tier2;
  return (
    <section>
      <h2 className="text-sm font-semibold mb-2 text-text-dim">Pulse &amp; Tier 2 · {summary.date}</h2>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="card card-tight">
          <div className="text-xs text-text-faint uppercase tracking-wide mb-2">Pulse precision</div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="Pulses" value={String(p.totalPulses)} />
            <Stat label="Reevals" value={String(p.reevaluates)} />
            <Stat label="→ Trades" value={String(p.reevaluatesProducingTrades)} />
          </div>
          <div className="mt-3 text-xs text-text-dim text-center">
            Precision: <span className="font-mono font-semibold text-text">{(p.precision * 100).toFixed(1)}%</span>
          </div>
        </div>
        <div className="card card-tight">
          <div className="text-xs text-text-faint uppercase tracking-wide mb-2">Tier 2 reevaluator</div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="Runs" value={String(t.runs)} />
            <Stat label="Trades" value={String(t.tradesSubmitted)} />
            <Stat label="Cost" value={usd(t.totalCost, 3)} />
          </div>
          <div className="mt-3 text-xs text-text-dim text-center">
            $/trade: <span className="font-mono font-semibold text-text">
              {t.costPerSubmitted != null ? usd(t.costPerSubmitted, 3) : '—'}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-base font-semibold font-mono tabular-nums">{value}</div>
      <div className="text-[10px] text-text-faint uppercase tracking-wide">{label}</div>
    </div>
  );
}
